import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { Interval } from 'teacher-ui-client/components/stable-table/basic-cells/duration-unit'
import { serialize as serializeSort, deserialize as deserializeSort } from '@blakeelearning/data-tables/utils/sorting'
import type { IntlService } from 'ember-intl'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import TooltipCell from 'teacher-ui-client/components/stable-table/basic-cells/tooltip'
import DurationUnitCell from 'teacher-ui-client/components/stable-table/basic-cells/duration-unit'
import type { CellObject } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import type Teacher from 'teacher-ui-client/models/teacher'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { BaseTableActions } from 'teacher-ui-client/components/base/table-actions'
import { eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import type {
  SimpleSummarySchool,
  SimpleSummaryTeacher,
} from 'teacher-ui-client/utils/student-events/fetchers/simple-summary'

interface Args {
  product: string
  schoolData?: SimpleSummarySchool
  teachers?: Teacher[]
  teachersData?: SimpleSummaryTeacher[]
}

interface Signature {
  Args: Args
}

type DataKey =
  | 'firstName'
  | 'lastName'
  | 'gradeName'
  | 'totalTime'
  | 'lessonsCompleted'
  | 'averageLexileGrowth'
  | 'averageTotalTimePerStudent'
  | 'averageLessonsCompletedPerStudent'

type DataKeyObject = Partial<Record<DataKey, string>>

const comparersForColumns = {
  firstName: 'sortByValue',
  lastName: 'sortByValue',
  // the grade column for the teacher tab displays a string of all the grades the teacher's students are in
  // eg. '1,2,5,12' which is not sortable by grade unlike the students tab which has a single grade value
  gradeName: 'sortByValue',
  totalTime: 'sortByNumber',
  lessonsCompleted: 'sortByNumber',
  averageTotalTimePerStudent: 'sortByNumber',
  averageLessonsCompletedPerStudent: 'sortByNumber',
  averageLexileGrowth: 'sortByNumber',
}

function cellClasses(product: string, dataKey: DataKey) {
  const colorClass =
    product === 'maths'
      ? 'bg-ms-green-50 group-hover:bg-ms-green-100'
      : 'bg-oceany-blue-50 group-hover:bg-oceany-blue-100'
  const mapping: DataKeyObject = {
    totalTime: 'text-center',
    lessonsCompleted: 'text-center',
    averageTotalTimePerStudent: 'text-center',
    averageLessonsCompletedPerStudent: 'text-center',
    averageLexileGrowth: 'text-center',
  }
  const customClass = mapping[dataKey] ?? ''
  return `${colorClass} ${customClass}`
}

function footerCellClasses(product: string, dataKey: DataKey) {
  const defaultClass = 'table-footer-cell border-t print:border-black bg-white sticky print:static'
  const colorClass = product === 'maths' ? 'border-ms-green-100' : 'border-oceany-blue-100'
  const mapping: DataKeyObject = {
    firstName: 'whitespace-nowrap', // stops the tooltips from wrapping
    totalTime: 'text-center',
    lessonsCompleted: 'text-center',
    averageTotalTimePerStudent: 'text-center',
    averageLessonsCompletedPerStudent: 'text-center',
    averageLexileGrowth: 'text-center',
  }
  const customClass = mapping[dataKey] ?? ''
  return `${defaultClass} ${colorClass} ${customClass}`
}

function headerClasses(product: string, dataKey: DataKey) {
  const defaultClass = product === 'maths' ? 'bg-ms-green-100' : 'bg-oceany-blue-100'
  const mapping: DataKeyObject = {
    totalTime: 'w-[15%] bg-purply-pink-350 text-white',
    lessonsCompleted: 'w-[15%] bg-juicy-orange-350 text-white',
    averageTotalTimePerStudent: 'w-[15%] bg-oceany-blue-300 text-white',
    averageLessonsCompletedPerStudent: 'w-[15%] bg-ms-green-350 text-white',
    averageLexileGrowth: 'w-[15%] bg-wl-blue-300 text-white',
  }
  return mapping[dataKey] ?? defaultClass
}

export class SummaryTablesTeachers extends Component<Signature> {
  @service intl!: IntlService

  @service subscriptionType!: SubscriptionTypeService

  @service gradeSets!: GradeSetsService

  @tracked sort?: string

  get averageCellsForColumns(): Record<DataKey, CellObject> {
    const { schoolData } = this.args

    // For the known empty cells in the footer, we want no text displayed
    const emptyCell = { formatEmptyConfig: { replacement: '' } }

    return {
      firstName: { value: this.intl.t(`reporting.summaryReportTitles.${this.args.product}.average`) },
      lastName: emptyCell,
      gradeName: emptyCell,
      totalTime: {
        component: DurationUnitCell,
        durationConfig: this.durationConfig,
        value: schoolData?.average_total_time_per_teacher,
      },
      lessonsCompleted: { value: schoolData?.average_lessons_completed_per_teacher },
      averageTotalTimePerStudent: {
        component: DurationUnitCell,
        durationConfig: this.durationConfig,
        value: schoolData?.average_total_time_per_student,
      },
      averageLessonsCompletedPerStudent: { value: schoolData?.average_lessons_completed_per_student },
      averageLexileGrowth: { value: schoolData?.average_lexile_growth ?? null },
    }
  }

  get columns() {
    return this.dataKeys.map((dataKey) => {
      const value = this.intl.t(`reporting.summaryReportTitles.${this.args.product}.${dataKey}`)

      const isTimeColumn = ['totalTime', 'averageTotalTimePerStudent'].includes(dataKey)
      const replaceZeroColumn = [
        'lessonsCompleted',
        'averageLessonsCompletedPerStudent',
        'averageLexileGrowth',
      ].includes(dataKey)

      const isAverageLexileGrowth = dataKey === 'averageLexileGrowth'
      const averageLexileGrowthTooltip = this.intl.t('reporting.summaryReportTitles.averageLexileGrowthTooltip')

      return {
        value,
        valuePath: dataKey,
        component: SortableCell,
        comparerKey: comparersForColumns[dataKey],
        cellComponent: isTimeColumn ? DurationUnitCell : undefined,
        durationConfig: isTimeColumn ? this.durationConfig : undefined,
        formatEmptyConfig: replaceZeroColumn ? { replaceZero: true } : undefined,
        tooltipValue: isAverageLexileGrowth ? averageLexileGrowthTooltip : undefined,
        classNames: `table-box table-box-header ${headerClasses(this.args.product, dataKey)}`,
        cellClassNames: `table-box table-box-cell ${cellClasses(this.args.product, dataKey)}`,
      }
    })
  }

  get dataKeys(): DataKey[] {
    const dataKeys: DataKey[] = [
      'firstName',
      'lastName',
      'gradeName',
      'totalTime',
      'lessonsCompleted',
      'averageTotalTimePerStudent',
      'averageLessonsCompletedPerStudent',
    ]
    return this.args.product === 'maths' ? dataKeys : [...dataKeys, 'averageLexileGrowth']
  }

  get durationConfig() {
    const splitDurationOptions = {
      max: Interval.Hours,
      min: Interval.Minutes,
      maxIntervals: 2,
    }
    const dataInterval = 'seconds'
    return { dataInterval, splitDurationOptions }
  }

  get footerRows() {
    const { dataKeys, averageCellsForColumns, totalCellsForColumns } = this

    const averageCells = dataKeys.map((dataKey) => {
      const cell = averageCellsForColumns[dataKey]
      cell.classNames = footerCellClasses(this.args.product, dataKey) + ' bottom-[40px]'
      return cell
    })
    const averageRow = { cells: averageCells }

    const totalsCells = dataKeys.map((dataKey) => {
      const cell = totalCellsForColumns[dataKey]
      cell.classNames = footerCellClasses(this.args.product, dataKey) + ' bottom-0'
      return cell
    })
    const totalsRow = { cells: totalsCells }

    return [averageRow, totalsRow]
  }

  get rows() {
    const teachers = this.args.teachers ?? []
    const teachersData = this.args.teachersData ?? []

    return teachers.map((teacher) => {
      const { id, firstName, lastName } = teacher
      const gradeName = this.getAllGradesForTeacher(teacher)

      const data: Partial<SimpleSummaryTeacher> =
        teachersData.find((teacherData) => String(teacherData.teacher_id) === id) ?? {}

      // We don't need any per-cell level configuration, so just use an empty array of cellObjects
      const cells = Array(this.dataKeys.length).fill({})

      return {
        firstName,
        lastName,
        gradeName,
        totalTime: data.total_time,
        lessonsCompleted: data.lessons_completed,
        averageTotalTimePerStudent: data.average_total_time_per_student,
        averageLessonsCompletedPerStudent: data.average_lessons_completed_per_student,
        averageLexileGrowth: data.average_lexile_growth,
        // classes for row striping and hovering the row of cells as a group (via tailwind group-hover)
        classNames: 'table-row-striped group',
        cells,
      }
    })
  }

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  get totalCellsForColumns(): Record<DataKey, CellObject> {
    const { schoolData } = this.args
    const { durationConfig, totalsTooltipMessage, intl } = this

    // For the known empty cells in the footer, we want no text displayed
    const emptyCell = { formatEmptyConfig: { replacement: '' } }

    return {
      firstName: {
        component: TooltipCell,
        value: intl.t(`reporting.summaryReportTitles.${this.args.product}.totals`),
        tooltipValue: totalsTooltipMessage,
      },
      lastName: emptyCell,
      gradeName: emptyCell,
      totalTime: {
        component: DurationUnitCell,
        durationConfig,
        value: schoolData?.total_time,
      },
      lessonsCompleted: { value: schoolData?.lessons_completed },
      averageTotalTimePerStudent: { value: intl.t('reporting.summaryReportTitles.na') },
      averageLessonsCompletedPerStudent: { value: intl.t('reporting.summaryReportTitles.na') },
      averageLexileGrowth: { value: intl.t('reporting.summaryReportTitles.na') },
    }
  }

  get totalsTooltipMessage() {
    return this.intl.t('reporting.summaryReportTitles.teacherTotalToolTipMessage')
  }

  @action
  onSortClick(sortingConfig: SortingConfig) {
    this.sortingConfig = sortingConfig
  }

  private getAllGradesForTeacher(teacher: Teacher) {
    if (!this.subscriptionType.current) return ''

    const gradePositions = teacher
      .schoolClassesForSubscriptionType(this.subscriptionType.current)
      .reduce<
        number[]
      >((gradePositions, schoolClass) => [...gradePositions, ...schoolClass.students.map((student) => student.gradePosition)], [])
    const uniqGradePositions = [...new Set(gradePositions)]
    const sortedGradePositions = uniqGradePositions.sort((a, b) => a - b)

    return sortedGradePositions.map((position) => this.gradeSets.findByPosition(position)?.name).join(', ')
  }

  <template>
    <div class="print:text-xs">
      <BaseTableActions
        @items={{this.rows}}
        @columns={{this.columns}}
        @sortingConfig={{this.sortingConfig}}
        @onSortClick={{this.onSortClick}}
        as |Table|
      >
        <div class="max-h-[700px] overflow-auto print:max-h-none">
          <Table
            exportable
            data-test-summary-table
            class="relative w-full border-separate border-spacing-0"
            @footerRows={{this.footerRows}}
            as |tbl|
          >
            <tbl.head class="table-row-group" as |h|>
              <h.row as |r|>
                <r.cell class="sticky top-0 print:static" />
              </h.row>
            </tbl.head>
            <tbl.body />
            <tbl.foot class="table-row-group print:border-t-2 print:border-black" />
          </Table>
        </div>
      </BaseTableActions>
      <div
        class="border-t p-2 text-xs font-bold
          {{if (eq @product 'maths') 'border-ms-green-100' 'border-oceany-blue-100'}}
          print:border-none"
      >
        <div>{{t "reporting.summaryReportTitles.totalTimeAndLessonsAverageMessage"}}</div>
        <div>{{t "reporting.summaryReportTitles.perStudentAverageMessage"}}</div>
      </div>
    </div>
  </template>
}

export default SummaryTablesTeachers
